import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, money, roundUp8, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { track } from "../lib/analytics";
import { fbTrack } from "../lib/fbpixel";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Coins, Send, Loader2, Check, Globe, AtSign, MessageCircle } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const DOMESTIC_FALLBACK = [
  { key: "ups_ground", label: "UPS Ground", eta: "5 to 8 business days", price: 7 },
  { key: "ups_2day", label: "UPS 2nd Day Air", eta: "2 business days", price: 13 },
  { key: "ups_nextday", label: "UPS Next Day Air", eta: "1 business day · order by 3PM EST", price: 40 },
];

const PAY_METHODS = [
  { key: "zelle", label: "Zelle", icon: Send, desc: "Send from your bank's Zelle. Instructions on the next screen and by email." },
  { key: "venmo", label: "Venmo", icon: AtSign, desc: "Pay our Venmo handle. Send as Friends and Family. Instructions after you order." },
  { key: "crypto", label: "Crypto · SOL / BTC / XRP", icon: Coins, desc: "Pay with Solana, Bitcoin, or XRP. Place your order and we send a wallet address for your preferred coin." },
  { key: "other", label: "Other · Message Us", icon: MessageCircle, desc: "No Zelle, Venmo, or crypto? Place your order and we message you to arrange payment." },
];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", phone: "", first_name: "", last_name: "",
    street: "", apt: "", city: "", state: "", zip: "", country: "US",
  });
  const [shipMethod, setShipMethod] = useState("ups_ground");
  const [payMethod, setPayMethod] = useState("zelle");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(false);
  const [appliedCode, setAppliedCode] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [placing, setPlacing] = useState(false);

  const [countries, setCountries] = useState([]);
  const [domesticMethods, setDomesticMethods] = useState(DOMESTIC_FALLBACK);
  const [threshold, setThreshold] = useState(200);
  const [freeMethod, setFreeMethod] = useState("ups_ground");

  useEffect(() => {
    api.get("/shipping/config").then(({ data }) => {
      setCountries(data.countries || []);
      if (data.domestic_methods?.length) setDomesticMethods(data.domestic_methods);
      if (data.free_shipping_threshold) setThreshold(data.free_shipping_threshold);
      if (data.free_shipping_method) setFreeMethod(data.free_shipping_method);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    track("begin_checkout");
    fbTrack("InitiateCheckout", {
      content_ids: items.map((i) => i.product?.slug).filter(Boolean),
      content_type: "product",
      num_items: items.reduce((s, i) => s + i.quantity, 0),
      value: +subtotal.toFixed(2),
      currency: "USD",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isUS = form.country === "US";
  const selectedCountry = countries.find((c) => c.code === form.country);
  const intlRate = selectedCountry?.rate ?? 40;

  const basePct = applied ? discountPct : 0;
  const discounted = basePct > 0 ? Math.min(roundUp8(subtotal * (1 - basePct / 100)), subtotal) : subtotal;
  const discount = +(subtotal - discounted).toFixed(2);
  const rawShip = isUS ? (domesticMethods.find((s) => s.key === shipMethod)?.price || 0) : intlRate;
  const eligibleFree = discounted >= threshold;
  const freeShip = isUS ? (eligibleFree && shipMethod === freeMethod) : eligibleFree;
  const shipCost = freeShip ? 0 : rawShip;
  const total = +(discounted + shipCost).toFixed(2);

  const onCountryChange = (e) => {
    const country = e.target.value;
    setForm((f) => ({ ...f, country, state: "" }));
    if (country !== "US") setShipMethod("international");
    else setShipMethod("ups_ground");
  };

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return toast.error("Enter a promo code");
    try {
      const { data } = await api.post("/discount/validate", { code });
      setApplied(true);
      setAppliedCode(data.code);
      setDiscountPct(data.pct);
      localStorage.setItem("cl_promo_code", data.code);
      toast.success("Discount applied");
    } catch {
      toast.error("Invalid promo code");
    }
  };

  const placeOrder = async () => {
    const required = ["email", "first_name", "last_name", "street", "city", "zip"];
    if (isUS) required.push("state");
    for (const k of required) {
      if (!String(form[k]).trim()) return toast.error("Please complete all required fields");
    }
    if (!confirm) return toast.error("Please confirm the research use agreement");
    setPlacing(true);
    try {
      const payload = {
        email: form.email,
        phone: form.phone,
        shipping: {
          first_name: form.first_name, last_name: form.last_name, street: form.street,
          apt: form.apt, city: form.city, state: form.state, zip: form.zip, country: form.country,
        },
        shipping_method: isUS ? shipMethod : "international",
        items: items.map((i) => ({
          product_id: i.product.id, tier_qty: i.tierQty, quantity: i.quantity,
          variant_label: i.variantLabel || null,
        })),
        payment_method: payMethod,
        discount_code: applied ? appliedCode : null,
        origin: window.location.origin,
      };
      const { data } = await api.post("/orders", payload);
      clearCart();
      navigate(`/order/${data.order_number}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPlacing(false);
    }
  };

  const inputCls = "w-full bg-[#0C1128] border border-white/25 text-white px-4 py-3.5 text-sm outline-none focus:border-electric placeholder:text-white/30 sq-sm";
  const labelCls = "block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-2";

  if (!items.length) {
    return (
      <div className="min-h-[70vh] bg-[#070B1C] flex items-center justify-center px-5 py-20">
        <div className="text-center" data-testid="checkout-empty">
          <p className="text-sm tracking-[0.2em] uppercase text-white/50 mb-6">Your cart is empty</p>
          <Link to="/" className="btn-primary inline-block px-9 py-4 text-xs tracking-[0.2em] uppercase font-bold">
            Browse the Lineup
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#070B1C] min-h-screen">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-8 lg:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold mb-8 text-white/70 hover:text-electric transition-colors duration-200" data-testid="checkout-back">
          <ArrowLeft size={14} /> Continue Shopping
        </Link>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-10">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* FORM */}
          <div className="lg:col-span-2 space-y-10">
            {/* Contact */}
            <section data-testid="section-contact">
              <h2 className="font-display font-bold text-lg tracking-tight uppercase text-white mb-5 pb-3 border-b border-white/12">1 · Contact</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Email Address *</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} data-testid="checkout-email" />
                </div>
                <div>
                  <label className={labelCls}>Phone Number <span className="text-white/35 normal-case tracking-normal font-normal">(optional)</span></label>
                  <input type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className={inputCls} data-testid="checkout-phone" />
                </div>
              </div>
            </section>

            {/* Shipping address */}
            <section data-testid="section-address">
              <h2 className="font-display font-bold text-lg tracking-tight uppercase text-white mb-5 pb-3 border-b border-white/12">2 · Shipping Address</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Country / Region *</label>
                  <select value={form.country} onChange={onCountryChange} className={inputCls} data-testid="checkout-country">
                    <option value="US">United States</option>
                    <optgroup label="International">
                      {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>First Name *</label>
                  <input value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="checkout-first-name" />
                </div>
                <div>
                  <label className={labelCls}>Last Name *</label>
                  <input value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="checkout-last-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Street Address *</label>
                  <input value={form.street} onChange={set("street")} className={inputCls} data-testid="checkout-street" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Apt, Suite, Unit (optional)</label>
                  <input value={form.apt} onChange={set("apt")} className={inputCls} data-testid="checkout-apt" />
                </div>
                <div>
                  <label className={labelCls}>City *</label>
                  <input value={form.city} onChange={set("city")} className={inputCls} data-testid="checkout-city" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    {isUS ? (
                      <>
                        <label className={labelCls}>State *</label>
                        <select value={form.state} onChange={set("state")} className={inputCls} data-testid="checkout-state">
                          <option value="">, </option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </>
                    ) : (
                      <>
                        <label className={labelCls}>State / Province <span className="text-white/35 normal-case tracking-normal font-normal">(optional)</span></label>
                        <input value={form.state} onChange={set("state")} className={inputCls} data-testid="checkout-state" />
                      </>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>{isUS ? "ZIP *" : "Postal Code *"}</label>
                    <input value={form.zip} onChange={set("zip")} className={inputCls} data-testid="checkout-zip" />
                  </div>
                </div>
              </div>
            </section>

            {/* Shipping method */}
            <section data-testid="section-shipping">
              <h2 className="font-display font-bold text-lg tracking-tight uppercase text-white mb-5 pb-3 border-b border-white/12">3 · Shipping Method</h2>
              {isUS ? (
                <div className="space-y-3">
                  {domesticMethods.map((s) => {
                    const active = shipMethod === s.key;
                    return (
                      <button
                        key={s.key}
                        onClick={() => setShipMethod(s.key)}
                        className={`w-full flex items-center justify-between border p-4 text-left transition-colors duration-200 sq-md sq-press ${active ? "border-electric bg-electric/10" : "border-white/20 hover:border-electric/60"}`}
                        data-testid={`ship-${s.key}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-4 h-4 rounded-full border ${active ? "border-electric bg-electric" : "border-white/40"}`} />
                          <div>
                            <div className="font-display font-bold text-sm uppercase text-white">{s.label}</div>
                            <div className="text-[11px] text-white/45 mt-0.5">{s.eta}</div>
                          </div>
                        </div>
                        <span className="font-display font-bold text-sm text-white">{eligibleFree && s.key === freeMethod ? "Free" : money(s.price)}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="w-full flex items-center justify-between border border-electric bg-electric/10 p-4 sq-md" data-testid="ship-international">
                    <div className="flex items-center gap-3">
                      <Globe size={18} className="text-electric" />
                      <div>
                        <div className="font-display font-bold text-sm uppercase text-white">International Shipping</div>
                        <div className="text-[11px] text-white/45 mt-0.5">Tracked · to {selectedCountry?.name || "your country"} · 7 to 18 business days</div>
                      </div>
                    </div>
                    <span className="font-display font-bold text-sm text-white">{eligibleFree ? "Free" : money(intlRate)}</span>
                  </div>
                  <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
                    Flat rate international shipping. <b className="text-white/70">Free over {money(threshold)}</b>. Any import duties or taxes are collected by your local customs on delivery.
                  </p>
                </div>
              )}
            </section>

            {/* Payment */}
            <section data-testid="section-payment">
              <h2 className="font-display font-bold text-lg tracking-tight uppercase text-white mb-5 pb-3 border-b border-white/12">4 · Payment</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {PAY_METHODS.map((m) => {
                  const active = payMethod === m.key;
                  const Icon = m.icon;
                  return (
                    <button
                      key={m.key}
                      onClick={() => setPayMethod(m.key)}
                      className={`border p-5 text-left transition-colors duration-200 sq-lg sq-press ${active ? "border-electric bg-electric/10" : "border-white/20 hover:border-electric/60"}`}
                      data-testid={`pay-${m.key}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={18} className="text-electric" />
                        <span className="font-display font-bold text-base uppercase text-white">{m.label}</span>
                        {active && <Check size={16} className="text-electric ml-auto" />}
                      </div>
                      <p className="text-[11px] text-white/50 leading-relaxed">{m.desc}</p>
                    </button>
                  );
                })}
              </div>

              <p className="text-[11px] text-white/40 mt-4 leading-relaxed">
                Your order is placed on hold as <span className="text-white/70">Awaiting Payment</span>. Send payment using the details on the confirmation screen, also emailed to you. We ship once it clears.
              </p>
            </section>
          </div>

          {/* SUMMARY */}
          <div>
            <div className="border border-white/15 bg-[#0B1024] p-6 lg:sticky lg:top-6 sq-lg" data-testid="checkout-summary">
              <h2 className="font-display font-bold text-lg tracking-tight uppercase text-white mb-5">Order Summary</h2>
              <div className="space-y-4 max-h-[280px] overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.id} className="flex gap-3" data-testid={`summary-item-${i.product.slug}`}>
                    <div className="w-14 h-14 border border-white/15 shrink-0 bg-[#121A3A] overflow-hidden sq-sm">
                      <img src={i.variantImage || i.product.image} alt={i.product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold uppercase tracking-tight text-white leading-tight">
                        {i.product.name}{i.variantLabel ? ` · ${i.variantLabel}` : ""}
                      </div>
                      <div className="text-[10px] text-electric-400 mt-0.5">
                        {i.tierQty > 1 ? `${i.tierQty} Pack` : "Single"} × {i.quantity}
                      </div>
                    </div>
                    <div className="font-display font-bold text-sm text-white">{money(i.packPrice * i.quantity)}</div>
                  </div>
                ))}
              </div>

              {/* coupon */}
              <div className="mt-6 flex gap-2">
                <input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  disabled={applied}
                  placeholder="PROMO CODE"
                  className="flex-1 bg-[#0C1128] border border-white/25 text-white px-4 py-3 text-xs tracking-[0.15em] uppercase outline-none focus:border-electric placeholder:text-white/30 disabled:opacity-50 sq-sm"
                  data-testid="checkout-coupon-input"
                />
                <button onClick={applyCoupon} disabled={applied} className="btn-ghost px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-40" data-testid="checkout-coupon-apply">
                  {applied ? "Applied" : "Apply"}
                </button>
              </div>

              <div className="mt-6 space-y-3 text-sm border-t border-white/12 pt-5">
                <div className="flex justify-between text-white/60">
                  <span className="text-xs tracking-[0.15em] uppercase">Subtotal</span>
                  <span className="text-white" data-testid="summary-subtotal">{money(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-electric-400">
                    <span className="text-xs tracking-[0.15em] uppercase">{appliedCode}</span>
                    <span data-testid="summary-discount">minus {money(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white/60">
                  <span className="text-xs tracking-[0.15em] uppercase">Shipping{isUS ? "" : " · Intl"}</span>
                  <span className="text-white" data-testid="summary-shipping">{shipCost === 0 ? "Free" : money(shipCost)}</span>
                </div>
                <div className="flex justify-between items-baseline border-t border-white/12 pt-4">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-white">Total</span>
                  <span className="font-display font-bold text-2xl tracking-tighter text-white" data-testid="summary-total">{money(total)}</span>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer mt-6" data-testid="checkout-confirm-label">
                <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-electric" data-testid="checkout-confirm" />
                <span className="text-[10px] leading-relaxed text-white/45">
                  I confirm all products are purchased for legitimate laboratory research only, not for human or animal use, and I agree to the{" "}
                  <Link to="/terms" className="text-electric underline">Terms of Service</Link>.
                </span>
              </label>

              <button
                onClick={placeOrder}
                disabled={placing}
                className="btn-primary w-full mt-5 py-4 text-xs tracking-[0.2em] uppercase font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="place-order"
              >
                {placing ? <><Loader2 size={16} className="animate-spin" /> Placing...</> : `Place Order · ${money(total)}`}
              </button>
              <p className="mt-4 flex items-center justify-center gap-2 text-[10px] tracking-[0.15em] uppercase text-white/35">
                <ShieldCheck size={12} /> Research use only · 21+
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
